import { BRAND } from '../config/brand';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type SiteLanguage } from './constants';

const SITE_URL = BRAND.siteUrl.replace(/\/$/, '');

/** Dil öneksiz kanonik yol: `/`, `/cozumler`, `/iletisim?konu=…` */
export function normalizeCanonicalPath(path: string): string {
  const withoutOrigin = path.replace(/^https?:\/\/[^/]+/i, '');
  const [pathname, ...rest] = withoutOrigin.split(/(?=[?#])/);
  const normalized = pathname.replace(/\/+$/, '') || '/';
  const suffix = rest.join('');
  return `${normalized}${suffix}`;
}

/**
 * URL'den dil ve dil öneksiz yolu ayırır.
 * `/tr/cozumler` → `{ lang: 'tr', path: '/cozumler' }`
 * `/cozumler` → `{ lang: null, path: '/cozumler' }`
 */
export function parseLocalizedPathname(pathname: string): {
  lang: SiteLanguage | null;
  path: string;
} {
  const clean = pathname.split(/(?=[?#])/)[0].replace(/\/+$/, '') || '/';
  const segments = clean.split('/').filter(Boolean);

  const first = segments[0];
  if (first === 'tr' || first === 'en') {
    const rest = segments.slice(1).join('/');
    return { lang: first, path: rest ? `/${rest}` : '/' };
  }

  return { lang: null, path: clean };
}

export function isSiteLanguage(value: string | undefined): value is SiteLanguage {
  return value === 'tr' || value === 'en';
}

/** Dahili yola dil öneki ekler: `/cozumler` → `/tr/cozumler` */
export function buildLocalizedPath(lang: SiteLanguage, canonicalPath: string): string {
  const normalized = normalizeCanonicalPath(canonicalPath);
  const [pathname, ...rest] = normalized.split(/(?=[?#])/);
  const suffix = rest.join('');

  if (pathname === '/') {
    return `/${lang}${suffix}`;
  }

  return `/${lang}${pathname}${suffix}`;
}

/** Tam mutlak URL — canonical ve hreflang için */
export function buildAbsoluteUrl(lang: SiteLanguage, canonicalPath: string): string {
  const localized = buildLocalizedPath(lang, canonicalPath);
  const [pathname] = localized.split(/(?=[?#])/);
  const base = `${SITE_URL}${pathname}`;
  const hashQuery = localized.slice(pathname.length);
  return `${base}${hashQuery}`;
}

export function buildHreflangAlternates(canonicalPath: string): Array<{ hreflang: string; href: string }> {
  const alternates: Array<{ hreflang: string; href: string }> = SUPPORTED_LANGUAGES.map((lang) => ({
    hreflang: lang,
    href: buildAbsoluteUrl(lang, canonicalPath),
  }));

  alternates.push({
    hreflang: 'x-default',
    href: buildAbsoluteUrl(DEFAULT_LANGUAGE, canonicalPath),
  });

  return alternates;
}

export function readPreferredLanguage(): SiteLanguage {
  if (typeof localStorage === 'undefined') return DEFAULT_LANGUAGE;
  const stored = localStorage.getItem('i18nextLng');
  return stored === 'en' ? 'en' : DEFAULT_LANGUAGE;
}
